import { useMemo } from "react";
import type { Lead } from "../Lead.types";

interface PeriodStats {
  added: number;
  contacted: number;
  won: number;
}

interface PeriodComparison {
  current: PeriodStats;
  previous: PeriodStats;
  changes: {
    added: number | null;
    contacted: number | null;
    won: number | null;
  };
}

export interface LeadStats {
  thisMonth: PeriodComparison;
  lastTwoWeeks: PeriodComparison;
  allTime: {
    total: number;
    won: number;
    conversionRate: number;
  };
}

function getStartOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function getStartOfPreviousMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() - 1, 1);
}

function getTwoWeeksAgo(date: Date): Date {
  const d = new Date(date);
  d.setDate(d.getDate() - 14);
  return d;
}

function getFourWeeksAgo(date: Date): Date {
  const d = new Date(date);
  d.setDate(d.getDate() - 28);
  return d;
}

function calculatePercentChange(current: number, previous: number): number | null {
  if (previous === 0) {
    return current > 0 ? 100 : null;
  }
  return Math.round(((current - previous) / previous) * 100);
}

function countLeadsInPeriod(
  leads: Lead[],
  startDate: Date,
  endDate: Date
): PeriodStats {
  const inPeriod = leads.filter((lead) => {
    const createdAt = new Date(lead.createdAt);
    return createdAt >= startDate && createdAt < endDate;
  });

  return {
    added: inPeriod.length,
    contacted: inPeriod.filter((l) =>
      ["CONTACTED", "FOLLOW_UP", "QUALIFIED", "WON", "LOST"].includes(l.status)
    ).length,
    won: inPeriod.filter((l) => l.status === "WON").length,
  };
}

export function useLeadStats(leads: Lead[]): LeadStats {
  return useMemo(() => {
    const now = new Date();

    const thisMonthStart = getStartOfMonth(now);
    const lastMonthStart = getStartOfPreviousMonth(now);
    const twoWeeksAgo = getTwoWeeksAgo(now);
    const fourWeeksAgo = getFourWeeksAgo(now);

    const thisMonthStats = countLeadsInPeriod(leads, thisMonthStart, now);
    const lastMonthStats = countLeadsInPeriod(leads, lastMonthStart, thisMonthStart);

    const lastTwoWeeksStats = countLeadsInPeriod(leads, twoWeeksAgo, now);
    const previousTwoWeeksStats = countLeadsInPeriod(leads, fourWeeksAgo, twoWeeksAgo);

    const totalLeads = leads.length;
    const wonLeads = leads.filter((l) => l.status === "WON").length;

    return {
      thisMonth: {
        current: thisMonthStats,
        previous: lastMonthStats,
        changes: {
          added: calculatePercentChange(thisMonthStats.added, lastMonthStats.added),
          contacted: calculatePercentChange(thisMonthStats.contacted, lastMonthStats.contacted),
          won: calculatePercentChange(thisMonthStats.won, lastMonthStats.won),
        },
      },
      lastTwoWeeks: {
        current: lastTwoWeeksStats,
        previous: previousTwoWeeksStats,
        changes: {
          added: calculatePercentChange(lastTwoWeeksStats.added, previousTwoWeeksStats.added),
          contacted: calculatePercentChange(lastTwoWeeksStats.contacted, previousTwoWeeksStats.contacted),
          won: calculatePercentChange(lastTwoWeeksStats.won, previousTwoWeeksStats.won),
        },
      },
      allTime: {
        total: totalLeads,
        won: wonLeads,
        conversionRate: totalLeads > 0 ? Math.round((wonLeads / totalLeads) * 1000) / 10 : 0,
      },
    };
  }, [leads]);
}
